import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Field, Input, Select, Textarea } from "@/components/ui/Form";
import {
  slugify,
  deriveProductCode,
  DELIVERY_TYPES,
  deliveryLabel,
  isAffiliate,
  requiresLink,
} from "@ag5r-store/shared";
import { Badge } from "@/components/ui/Badge";
import { MonoText } from "@/components/ui/CopyButton";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { formatIDR, formatDate, formatDuration } from "@/lib/format";
import type { DeliveryType } from "@ag5r-store/shared";

interface AdminProduct {
  id: string;
  name: string;
  product_code: string;
  slug: string;
  delivery_type: string;
  is_featured: number;
  /** 0 = disabled: hilang dari katalog publik, tetap tampil di sini. */
  is_active: number;
  category: string | null;
  created_at: string;
}

interface AdminVariant {
  id: string;
  product_id: string;
  name: string;
  duration_days: number;
  price: number;
  /** 0 = nonaktif: hilang dari katalog publik, tetap tampil di sini. */
  is_active: number;
}

export function AdminProductsPage() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [variantsOpen, setVariantsOpen] = useState<AdminProduct | null>(null);
  const [editing, setEditing] = useState<AdminProduct | null>(null);
  const [viewing, setViewing] = useState<AdminProduct | null>(null);
  /** id produk yang sedang diproses — mencegah klik ganda pada baris itu. */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Mode arsip — satu-satunya tempat produk terarsip bisa dilihat & dipulihkan. */
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = showArchived
        ? await api.listArchivedProducts()
        : await api.listProductsAdmin();
      setProducts(data as AdminProduct[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  async function restore(p: AdminProduct) {
    setBusyId(p.id);
    setError(null);
    try {
      await api.restoreProduct(p.id);
      window.alert(
        `"${p.name}" dipulihkan dalam keadaan NONAKTIF.\n\n` +
          "Periksa harga dan stoknya, lalu tekan ENABLE agar tampil kembali di katalog.",
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal memulihkan produk");
    } finally {
      setBusyId(null);
    }
  }

  useEffect(() => {
    if (!authLoading && user?.role === "owner") load();
  }, [user, authLoading, load]);

  const toggleActive = useCallback(async (p: AdminProduct) => {
    setBusyId(p.id);
    setError(null);
    try {
      await api.setProductActive(p.id, p.is_active === 0);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal mengubah status produk");
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const removeProduct = useCallback(async (p: AdminProduct) => {
    // Server yang memutuskan hapus permanen vs arsip, tapi user berhak tahu
    // sebelum menekan — ini aksi yang tidak bisa dibatalkan.
    const ok = window.confirm(
      `Hapus "${p.name}"?\n\n` +
        "Produk yang belum pernah dipesan akan dihapus permanen.\n" +
        "Produk yang sudah pernah terjual akan diarsipkan agar lisensi " +
        "pembeli tetap utuh.",
    );
    if (!ok) return;

    setBusyId(p.id);
    setError(null);
    try {
      const res = await api.deleteProduct(p.id);
      if (res.mode === "archived") {
        window.alert(
          `"${p.name}" diarsipkan, bukan dihapus permanen — ada ${res.order_count} order yang merujuk produk ini.`,
        );
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menghapus produk");
    } finally {
      setBusyId(null);
    }
  }, [load]);

  if (authLoading || (loading && !user)) return <LoadingState />;
  if (!user || user.role !== "owner") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-failed">[ ACCESS_DENIED ]</MonoText>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="section-heading">PRODUCT_MANAGER</h1>
          <h2 className="mt-3 font-sans text-3xl text-ink-primary">Catalog Control</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showArchived ? "primary" : "secondary"}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? "← Produk Aktif" : "Lihat Arsip"}
          </Button>
          {!showArchived && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + NEW PRODUCT
            </Button>
          )}
        </div>
      </div>

      {showArchived && (
        <div className="mb-6 border border-border-strong bg-elevated rounded-lg px-5 py-4">
          <p className="font-sans text-sm text-ink-primary">
            Produk di sini sudah diarsip — tidak tampil di katalog maupun daftar produk aktif.
          </p>
          <p className="mt-1 font-sans text-xs text-ink-muted">
            Memulihkannya mengembalikan produk dalam keadaan nonaktif, supaya Anda
            sempat memeriksa harga dan stok sebelum dijual lagi.
          </p>
        </div>
      )}

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : products.length === 0 ? (
        <Panel>
          <MonoText size="sm" color="ink-muted" className="block mb-2">NO_PRODUCTS_YET</MonoText>
          <MonoText size="xs" color="ink-dim">Create your first product to populate the catalog.</MonoText>
        </Panel>
      ) : (
        <Panel padded={false}>
          {/* 7 kolom + 5 tombol aksi per baris tidak akan pernah muat di layar
              sempit. Tanpa pembungkus ini tabel mendorong SELURUH halaman
              melebar — bukan hanya dirinya — sehingga header dan footer ikut
              bergeser. min-w menjaga kolom tetap terbaca, bukan gepeng.
              Pola sama sudah dipakai AdminOrdersPage & AdminInventoryPage. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[60rem]">
            <thead>
              <tr className="border-b border-border bg-elevated">
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">CODE</th>
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">NAME</th>
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">TYPE</th>
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">CAT</th>
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">STATUS</th>
                <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">CREATED</th>
                <th className="text-right font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-border last:border-b-0 hover:bg-elevated ${
                    p.is_active === 0 ? "opacity-55" : ""
                  }`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-accent">{p.product_code}</td>
                  <td className="px-4 py-3">
                    <div className="font-sans text-sm text-ink-primary">{p.name}</div>
                    <div className="font-mono text-2xs text-ink-dim">{p.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={isAffiliate(p.delivery_type as DeliveryType) ? "pending" : "active"}>
                      {deliveryLabel(p.delivery_type)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {p.category ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={p.is_active === 0 ? "expired" : "active"}>
                      {p.is_active === 0 ? "DISABLED" : "LIVE"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {formatDate(p.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {showArchived ? (
                        <Button
                          variant="primary"
                          disabled={busyId === p.id}
                          onClick={() => void restore(p)}
                        >
                          PULIHKAN
                        </Button>
                      ) : (
                      <>
                      <Button variant="ghost" onClick={() => setViewing(p)}>VIEW</Button>
                      <Button variant="ghost" onClick={() => setEditing(p)}>EDIT</Button>
                      <Button variant="ghost" onClick={() => setVariantsOpen(p)}>VARIANTS</Button>
                      <Button
                        variant="ghost"
                        onClick={() => void toggleActive(p)}
                        disabled={busyId === p.id}
                      >
                        {p.is_active === 0 ? "ENABLE" : "DISABLE"}
                      </Button>
                      <button
                        type="button"
                        disabled={busyId === p.id}
                        onClick={() => void removeProduct(p)}
                        className="px-3 py-1.5 font-mono text-2xs uppercase tracking-widest
                                   border border-transparent text-ink-muted
                                   hover:text-hot hover:border-hot disabled:opacity-40"
                      >
                        DELETE
                      </button>
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </Panel>
      )}

      {createOpen && (
        <ProductFormModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={load}
        />
      )}

      {editing && (
        <ProductFormModal
          open
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      {viewing && (
        <ProductViewModal product={viewing} onClose={() => setViewing(null)} />
      )}

      {variantsOpen && (
        <VariantsModal
          product={variantsOpen}
          onClose={() => setVariantsOpen(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// Product Form Modal — dipakai untuk CREATE dan EDIT
// ============================================================

/** URL yang diketik manual: satu per baris, baris kosong dibuang. */
function parseManualUrls(text: string): string[] {
  return text.split("\n").map((s) => s.trim()).filter(Boolean);
}

function ProductFormModal({
  open,
  product,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Kosong = mode create. Terisi = mode edit. */
  product?: AdminProduct;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = Boolean(product);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(isEdit);

  // Form state
  const [name, setName] = useState(product?.name ?? "");
  const [code, setCode] = useState(product?.product_code ?? "");
  const [slug, setSlug] = useState(product?.slug ?? "");
  /**
   * Slug hanya mengikuti nama selama owner belum pernah mengetiknya manual.
   * Begitu disentuh, jangan ditimpa — kalau tidak, edit nama akan diam-diam
   * mengganti URL produk yang mungkin sudah dibagikan.
   */
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [description, setDescription] = useState("");
  const [imagesText, setImagesText] = useState(""); // multiline URLs
  const [downloadUrl, setDownloadUrl] = useState("");
  const [deliveryType, setDeliveryType] = useState<DeliveryType>("SERIAL_KEY");
  const [category, setCategory] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mode edit: daftar produk tidak membawa description/images, jadi ambil
  // detail lengkapnya dulu.
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.getProductAdmin(product.id);
        if (cancelled) return;
        setDescription(d.description ?? "");
        setDownloadUrl(d.download_url ?? "");
        setDeliveryType(d.delivery_type as DeliveryType);
        setCategory(d.category ?? "");
        setIsFeatured(d.is_featured === 1);
        try {
          const imgs = JSON.parse(d.images || "[]");
          if (Array.isArray(imgs)) setUploadedImages(imgs as string[]);
        } catch {
          // images tersimpan sebagai JSON string; kalau korup, mulai kosong
          // daripada menggagalkan seluruh form.
          setUploadedImages([]);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Gagal memuat detail produk");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [product]);

  // Slug otomatis mengikuti nama — hanya di mode create dan selama belum
  // diketik manual.
  useEffect(() => {
    if (slugTouched) return;
    setSlug(slugify(name));
  }, [name, slugTouched]);

  function reset() {
    setName(""); setCode(""); setSlug(""); setDescription("");
    setImagesText(""); setDownloadUrl(""); setDeliveryType("SERIAL_KEY");
    setCategory(""); setIsFeatured(false); setUploadedImages([]);
    setSlugTouched(false); setError(null);
  }

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const sig = await api.getCloudinarySign();
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        form.append("api_key", sig.api_key);
        form.append("timestamp", String(sig.timestamp));
        form.append("signature", sig.signature);
        form.append("folder", sig.folder);
        const res = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: "POST", body: form },
        );
        if (!res.ok) {
          // Cloudinary mengirim { error: { message } } — tanpa membacanya,
          // penyebab aslinya (preset salah, folder terkunci, signature basi)
          // hilang sama sekali dan hanya tersisa "upload failed".
          let detail = `HTTP ${res.status}`;
          try {
            const errJson = (await res.json()) as { error?: { message?: string } };
            if (errJson?.error?.message) detail = errJson.error.message;
          } catch {
            // Body bukan JSON — pakai status code saja.
          }
          throw new Error(`Cloudinary menolak "${file.name}": ${detail}`);
        }
        const json = (await res.json()) as { secure_url: string };
        setUploadedImages((prev) =>
          prev.includes(json.secure_url) ? prev : [...prev, json.secure_url],
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      // Reset input file — tanpa ini, memilih file yang sama lagi (misal
      // setelah dihapus) tidak memicu onChange sama sekali.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  /**
   * Hapus by index, bukan by URL: data lama bisa berisi URL kembar dan
   * pencocokan by value akan menghapus semuanya sekaligus.
   */
  function removeImage(index: number) {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  }

  /**
   * Storefront selalu memakai images[0] sebagai thumbnail/cover, jadi
   * "jadikan cover" = pindahkan gambar itu ke urutan pertama.
   */
  function makeCover(index: number) {
    setUploadedImages((prev) => {
      if (index <= 0 || index >= prev.length) return prev;
      const next = [...prev];
      const [picked] = next.splice(index, 1);
      return [picked, ...next];
    });
  }

  /** Tarik URL manual ke daftar terkelola agar bisa dihapus / dijadikan cover. */
  function addManualUrls() {
    const urls = parseManualUrls(imagesText);
    if (urls.length === 0) return;
    setUploadedImages((prev) => [...prev, ...urls.filter((u) => !prev.includes(u))]);
    setImagesText("");
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      // Gabung URL dari upload + manual input. Urutan dipertahankan apa
      // adanya: elemen pertama dipakai storefront sebagai cover.
      const manualUrls = parseManualUrls(imagesText);
      const allImages = [
        ...uploadedImages,
        ...manualUrls.filter((u) => !uploadedImages.includes(u)),
      ];

      const payload = {
        name: name.trim(),
        // slug & product_code dikosongkan = server yang membangkitkan dan
        // menjamin keunikannya.
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        images: allImages,
        download_url: downloadUrl.trim() || undefined,
        delivery_type: deliveryType,
        is_featured: isFeatured,
        category: category.trim() || undefined,
      };

      if (product) {
        await api.updateProduct(product.id, payload);
      } else {
        await api.createProduct(payload);
      }

      reset();
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create product");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={isEdit ? `EDIT — ${product!.product_code}` : "CREATE NEW PRODUCT"}
      subtitle={
        isEdit
          ? "Product code tidak bisa diubah — kode itu sudah tercetak di lisensi yang terbit."
          : "Slug & product code dibuat otomatis dari nama."
      }
      maxWidth="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>[ CANCEL ]</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={detailLoading}
          >
            {isEdit ? "SAVE CHANGES" : "CREATE PRODUCT"}
          </Button>
        </>
      }
    >
      {detailLoading ? (
        <LoadingState label="LOADING_PRODUCT" />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="NAME" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Windows 11 Pro" />
        </Field>
        <Field
          label="PRODUCT CODE"
          hint={isEdit ? "Terkunci setelah produk dibuat." : "Dibuat otomatis dari nama — dijamin unik."}
        >
          <Input
            value={isEdit ? code : (code || deriveProductCode(name || "—"))}
            readOnly
            disabled
            placeholder="AUTO"
          />
        </Field>
        <Field
          label="SLUG"
          hint={slugTouched ? "Diketik manual — tidak lagi ikut nama." : "Mengikuti nama otomatis."}
        >
          <Input
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
            }}
            placeholder="windows-11-pro"
          />
        </Field>
        <Field
          label="DELIVERY TYPE"
          required
          hint={
            isAffiliate(deliveryType)
              ? "Affiliate: pembeli dilempar ke halaman mitra. Tidak ada keranjang, pembayaran, maupun stok di sini."
              : undefined
          }
        >
          <Select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value as DeliveryType)}>
            {DELIVERY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t} — {deliveryLabel(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="CATEGORY">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="software / streaming / tools" />
        </Field>
        <Field label="FEATURED">
          <label className="flex items-center gap-2 cursor-pointer py-2">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="w-4 h-4 accent-terminal border-border bg-void"
            />
            <MonoText size="xs" color="ink-muted">show on landing</MonoText>
          </label>
        </Field>
        <div className="md:col-span-2">
          <Field label="DESCRIPTION" hint="Teks polos. Baris baru tetap dipertahankan saat ditampilkan.">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Tulis deskripsi produk…"
            />
          </Field>
        </div>

        {/* Selalu tampil, tapi artinya berbeda per tipe. AFFILIATE: halaman
            tujuan pembeli. STATIC_LINK_ONLY: produknya sendiri. Akun & serial
            key: link aplikasi/panduan yang tetap dibutuhkan pembeli — akun
            premium tanpa aplikasinya tidak ada gunanya. */}
        <div className="md:col-span-2">
          <Field
            label={
              isAffiliate(deliveryType)
                ? "LINK AFFILIATE"
                : deliveryType === "STATIC_LINK_ONLY"
                  ? "LINK PRODUK"
                  : "LINK APLIKASI / AKSES (OPSIONAL)"
            }
            required={requiresLink(deliveryType)}
            hint={
              isAffiliate(deliveryType)
                ? "Halaman tujuan saat pembeli menekan “Lanjut ke Mitra”. Shopee, Tokopedia, Lynk, atau halaman mana pun yang Anda tentukan."
                : deliveryType === "STATIC_LINK_ONLY"
                  ? "Ini yang diterima pembeli. Google Drive, GitHub Releases, Mega, dll."
                  : "Tempat unduh aplikasi atau panduan pemakaian. Ditampilkan ke pembeli setelah pembayaran."
            }
          >
            <Input
              value={downloadUrl}
              onChange={(e) => setDownloadUrl(e.target.value)}
              placeholder={
                isAffiliate(deliveryType)
                  ? "https://shopee.co.id/product/..."
                  : "https://drive.google.com/file/d/..."
              }
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field
            label="IMAGES"
            hint="Gambar pertama dipakai sebagai cover di katalog. Pilih COVER untuk memindahkannya ke urutan pertama."
          >
            <div className="space-y-3">
              <div>
                <label className="font-mono text-2xs uppercase tracking-widest text-ink-muted block mb-1.5">
                  UPLOAD TO CLOUDINARY
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleUpload(e.target.files)}
                  disabled={uploading}
                  className="block w-full text-2xs font-mono text-ink-muted file:bg-void file:border file:border-border file:px-3 file:py-1.5 file:text-ink-primary file:font-mono file:mr-3"
                />
                {uploading && <MonoText size="xs" color="ink-muted" className="mt-1.5 block cursor-blink">UPLOADING...</MonoText>}
                {uploadedImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {uploadedImages.map((url, i) => (
                      <div key={`${i}-${url}`} className="space-y-1">
                        <div
                          className={`relative aspect-square border overflow-hidden bg-panel ${
                            i === 0 ? "border-terminal" : "border-border"
                          }`}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          {i === 0 && (
                            <span className="absolute top-0 left-0 bg-terminal text-void font-mono text-2xs uppercase tracking-widest px-1 py-0.5">
                              cover
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            title="Hapus gambar ini"
                            aria-label={`Hapus gambar ${i + 1}`}
                            className="absolute top-0 right-0 bg-void/90 border-l border-b border-border text-ink-muted font-mono text-2xs px-1.5 py-0.5 hover:text-status-failed hover:border-status-failed"
                          >
                            ✕
                          </button>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="product-cover"
                            checked={i === 0}
                            onChange={() => makeCover(i)}
                            className="w-3 h-3 accent-terminal"
                          />
                          <MonoText size="xs" color={i === 0 ? "terminal" : "ink-muted"}>
                            COVER
                          </MonoText>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="font-mono text-2xs uppercase tracking-widest text-ink-muted block mb-1.5">
                  OR PASTE URLS (one per line)
                </label>
                <Textarea
                  value={imagesText}
                  onChange={(e) => setImagesText(e.target.value)}
                  rows={3}
                  placeholder={"https://res.cloudinary.com/.../img1.png\nhttps://..."}
                />
                {/* Tanpa tombol ini, URL manual baru muncul setelah disimpan —
                    jadi tidak bisa dihapus atau dijadikan cover dari sini. */}
                <button
                  type="button"
                  onClick={addManualUrls}
                  disabled={parseManualUrls(imagesText).length === 0}
                  className="mt-1.5 font-mono text-2xs tracking-widest uppercase border border-border-strong px-2 py-1 disabled:opacity-30 hover:border-terminal hover:text-terminal"
                >
                  [ + tambah ke daftar ]
                </button>
              </div>
            </div>
          </Field>
        </div>
      </div>
      )}

      {error && (
        <div className="mt-4 border border-status-failed p-3">
          <MonoText size="xs" color="status-failed">! {error}</MonoText>
        </div>
      )}
    </Modal>
  );
}

// ============================================================
// Product View Modal — detail read-only + varian & stok
// ============================================================

function ProductViewModal({
  product,
  onClose,
}: {
  product: AdminProduct;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<Awaited<
    ReturnType<typeof api.getProductAdmin>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.getProductAdmin(product.id);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Gagal memuat detail");
      }
    })();
    return () => { cancelled = true; };
  }, [product.id]);

  let images: string[] = [];
  try {
    const parsed = JSON.parse(detail?.images ?? "[]");
    if (Array.isArray(parsed)) images = parsed as string[];
  } catch {
    images = [];
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`VIEW — ${product.product_code}`}
      subtitle={product.name}
      maxWidth="max-w-3xl"
      footer={<Button variant="ghost" onClick={onClose}>[ CLOSE ]</Button>}
    >
      {error ? (
        <ErrorState error={error} />
      ) : !detail ? (
        <LoadingState label="LOADING_DETAIL" />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Meta label="SLUG" value={detail.slug} />
            <Meta label="TYPE" value={detail.delivery_type} />
            <Meta label="CATEGORY" value={detail.category ?? "—"} />
            <Meta label="STATUS" value={detail.is_active === 0 ? "DISABLED" : "LIVE"} />
          </div>

          {images.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {images.map((url) => (
                <div key={url} className="w-24 h-24 border border-border bg-panel overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div>
            <MonoText size="xs" color="ink-muted" className="block mb-2">DESCRIPTION</MonoText>
            {detail.description ? (
              <p className="font-sans text-sm text-ink-primary leading-relaxed whitespace-pre-wrap">
                {detail.description}
              </p>
            ) : (
              <MonoText size="xs" color="ink-dim">—</MonoText>
            )}
          </div>

          <div>
            <MonoText size="xs" color="ink-muted" className="block mb-2">
              VARIANTS ({detail.variants.length})
            </MonoText>
            {detail.variants.length === 0 ? (
              <MonoText size="xs" color="ink-dim">Belum ada varian.</MonoText>
            ) : (
              <div className="border border-border divide-y divide-border">
                {detail.variants.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="font-sans text-sm text-ink-primary">{v.name}</div>
                      <MonoText size="xs" color="ink-dim">
                        {formatDuration(v.duration_days)}
                      </MonoText>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-accent">{formatIDR(v.price)}</div>
                      <MonoText size="xs" color="ink-dim">stok {v.stock_available}</MonoText>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border bg-panel px-3 py-2">
      <MonoText size="xs" color="ink-muted" className="block">{label}</MonoText>
      <div className="font-mono text-xs text-ink-primary mt-0.5 break-all">{value}</div>
    </div>
  );
}

// ============================================================
// Variants Modal — list + add new
// ============================================================

function VariantsModal({ product, onClose }: { product: AdminProduct; onClose: () => void }) {
  const [variants, setVariants] = useState<AdminVariant[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDuration, setEditDuration] = useState("30");
  const [editPrice, setEditPrice] = useState("");
  /** id varian yang sedang diproses — mencegah klik ganda pada baris itu. */
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New variant form
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("30");
  /** "onetime" disimpan sebagai duration_days = 0. */
  const [billing, setBilling] = useState<"recurring" | "onetime">("recurring");
  const [price, setPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listVariantsAdmin(product.id);
      setVariants(data as AdminVariant[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load variants");
    } finally {
      setLoading(false);
    }
  }, [product.id]);

  useEffect(() => {
    load();
  }, [load]);

  function startEdit(v: AdminVariant) {
    setEditingId(v.id);
    setEditName(v.name);
    setEditDuration(String(v.duration_days));
    setEditPrice(String(v.price));
    setError(null);
  }

  async function saveEdit(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.updateVariant(id, {
        name: editName.trim(),
        duration_days: parseInt(editDuration, 10) || 0,
        price: parseInt(editPrice, 10),
      });
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menyimpan varian");
    } finally {
      setBusy(null);
    }
  }

  async function toggleActive(v: AdminVariant) {
    setBusy(v.id);
    setError(null);
    try {
      await api.setVariantActive(v.id, v.is_active === 0);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal mengubah status varian");
    } finally {
      setBusy(null);
    }
  }

  async function removeVariant(v: AdminVariant) {
    const ok = window.confirm(
      `Hapus paket "${v.name}"?\n\n` +
        "Paket yang belum pernah dipesan akan dihapus permanen beserta stoknya.\n" +
        "Paket yang sudah punya riwayat pesanan hanya dinonaktifkan, agar " +
        "pesanan lama tetap utuh.",
    );
    if (!ok) return;

    setBusy(v.id);
    setError(null);
    try {
      const res = await api.deleteVariant(v.id);
      if (res.mode === "disabled") {
        window.alert(
          `"${v.name}" dinonaktifkan, bukan dihapus — ada ${res.order_count} pesanan yang merujuk paket ini.`,
        );
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menghapus varian");
    } finally {
      setBusy(null);
    }
  }

  async function handleAdd() {
    setSubmitting(true);
    setError(null);
    try {
      // "Sekali Bayar" disimpan sebagai duration_days = 0 — tidak ada tanggal
      // kedaluwarsa yang dihitung untuk entitlement-nya.
      const dur = billing === "onetime" ? 0 : parseInt(duration, 10);
      const pr = parseInt(price, 10);
      if (billing === "recurring" && (isNaN(dur) || dur < 1)) {
        throw new Error("Masa berlaku minimal 1 hari");
      }
      if (isNaN(pr) || pr <= 0) throw new Error("Harga tidak valid");
      await api.createVariant({
        product_id: product.id,
        name: name.trim(),
        duration_days: dur,
        price: pr,
      });
      setName(""); setDuration("30"); setPrice("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add variant");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`VARIANTS — ${product.product_code}`}
      subtitle={product.name}
      maxWidth="max-w-2xl"
      footer={
        <Button variant="ghost" onClick={onClose}>[ CLOSE ]</Button>
      }
    >
      {/* Existing variants */}
      <div className="terminal-label mb-2">EXISTING_VARIANTS ({variants.length})</div>
      {loading ? (
        <LoadingState />
      ) : variants.length === 0 ? (
        <div className="border border-dashed border-border p-4 text-center font-mono text-2xs text-ink-dim uppercase">
          NO_VARIANTS_YET
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {variants.map((v) =>
            editingId === v.id ? (
              <div key={v.id} className="p-3 grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-2">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                <Input
                  type="number"
                  min="0"
                  value={editDuration}
                  onChange={(e) => setEditDuration(e.target.value)}
                  placeholder="0 = Sekali Bayar"
                />
                <Input
                  type="number"
                  min="1"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                />
                <div className="flex gap-1">
                  <Button variant="primary" onClick={() => void saveEdit(v.id)} loading={busy === v.id}>
                    Simpan
                  </Button>
                  <Button variant="ghost" onClick={() => setEditingId(null)}>Batal</Button>
                </div>
              </div>
            ) : (
              <div
                key={v.id}
                className={`p-3 flex flex-wrap items-center justify-between gap-3 ${
                  v.is_active === 0 ? "opacity-55" : ""
                }`}
              >
                <div className="min-w-0">
                  <MonoText size="sm" color="ink-primary">{v.name}</MonoText>
                  <MonoText size="xs" color="ink-muted" className="block mt-0.5">
                    {formatDuration(v.duration_days)}
                    {v.is_active === 0 && " · NONAKTIF"}
                  </MonoText>
                </div>
                <div className="flex items-center gap-2">
                  <MonoText size="sm" color="terminal">{formatIDR(v.price)}</MonoText>
                  <Button variant="ghost" onClick={() => startEdit(v)}>Edit</Button>
                  <Button
                    variant="ghost"
                    disabled={busy === v.id}
                    onClick={() => void toggleActive(v)}
                  >
                    {v.is_active === 0 ? "Aktifkan" : "Nonaktifkan"}
                  </Button>
                  <button
                    type="button"
                    disabled={busy === v.id}
                    onClick={() => void removeVariant(v)}
                    className="px-3 py-1.5 font-mono text-2xs uppercase tracking-widest
                               border border-transparent text-ink-muted
                               hover:text-hot hover:border-hot disabled:opacity-40"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {/* Add new */}
      <div className="mt-6">
        <div className="terminal-label mb-3">TAMBAH PAKET</div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1.4fr_1fr_1fr_auto] gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nama paket, mis. 30 Hari"
          />
          <Select
            value={billing}
            onChange={(e) => setBilling(e.target.value as "recurring" | "onetime")}
          >
            <option value="recurring">Ada masa berlaku</option>
            <option value="onetime">Sekali Bayar</option>
          </Select>
          {billing === "recurring" ? (
            <Input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="jumlah hari"
              min="1"
            />
          ) : (
            // Tanpa masa berlaku — tidak ada angka hari yang perlu diisi.
            <Input value="Tanpa kedaluwarsa" readOnly disabled />
          )}
          <Input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="harga (Rp)"
            min="1"
          />
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={!name || !price}
            loading={submitting}
          >
            + ADD
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-4 border border-status-failed p-3">
          <MonoText size="xs" color="status-failed">! {error}</MonoText>
        </div>
      )}
    </Modal>
  );
}